import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, TrendingUp, Newspaper, Globe2, CheckCircle2, Lock } from "lucide-react";
import { api } from "../lib/api";
import { Footer } from "../components/Footer";
import { LanguageToggle } from "../components/LanguageToggle";
import { useReactiveLang } from "../lib/i18n";
import { toast } from "sonner";

const LANGS = [
  { code: "en", label: "English" },
  { code: "nl", label: "Nederlands" },
  { code: "es", label: "Español" },
];

// Locked 2026-02-27 during the Ad Slot Model Reconciliation.
// The 5 formats and their order are the canonical exclusive-inventory
// set (20 slots = 4 islands × 5 formats). In-Feed and Island Essentials
// sponsorships are separate products managed via Admin → Ad Slots and
// intentionally NOT surfaced here — see /app/memory/PRD.md.
const AD_SLOTS = [
  { id: "leaderboard",       key: "leaderboard" },
  { id: "sidebar_tall",      key: "sidebar_tall" },
  { id: "rectangle",         key: "rectangle" },
  { id: "sidebar_sticky",    key: "sidebar_sticky" },
  { id: "sponsored_category",key: "sponsored" },
];

const ISLAND_META = {
  aruba:      { flag: "🇦🇼", name: "Aruba" },
  curacao:    { flag: "🇨🇼", name: "Curaçao" },
  bonaire:    { flag: "🇧🇶", name: "Bonaire" },
  stmaarten:  { flag: "🇸🇽", name: "St. Maarten" },
};

const COPY = {
  en: {
    title: "Advertise with IslandsDaily",
    subtitle: "Reach English, Dutch, and Spanish-speaking readers across Aruba, Curaçao, Bonaire, and St. Maarten — the tourists, expats, and locals who follow the islands.",
    stats_h: "Live traction",
    stat_subs: "WhatsApp subscribers",
    stat_pv: "Pageviews (last 30 days)",
    stat_art: "Articles published (last 30 days)",
    stat_islands: "Islands covered",
    upcoming_h: "Coming next",
    upcoming_p: "New islands launching soon. Reserve your ad slot at pre-launch pricing before we go live.",
    wait_h: "Get notified at launch",
    wait_p: "Drop your email and we'll ping you the moment a new island goes live.",
    wait_email: "your@email.com",
    wait_pick: "Which island?",
    wait_all: "Notify me about all new islands",
    wait_submit: "Join waitlist",
    wait_success: "You're on the list — we'll email you at launch.",
    formats_h: "Available ad formats",
    formats: {
      leaderboard: {
        name: "Leaderboard · 728 × 90",
        desc: "Above the fold on every page. High-visibility banner right below the hero article.",
      },
      rectangle: {
        name: "Rectangle · 300 × 250",
        desc: "Sidebar top position. First thing readers see when scanning the feed on desktop.",
      },
      sidebar_tall: {
        name: "Half-Page · 300 × 600",
        desc: "Tall sidebar creative. Dominant, cannot-miss placement while reading.",
      },
      sidebar_sticky: {
        name: "Sidebar Sticky · 300 × 250",
        desc: "Follows the reader down the page. Every article view = every impression.",
      },
      sponsored: {
        name: "Sponsored Category",
        desc: "Own a category (Tourism, Business, Sports…) with your brand next to every headline in it.",
      },
    },
    pricing_h: "Pricing",
    pricing_standard_label: "Standard rate",
    pricing_launch_label: "Launch Partner rate",
    pricing_launch_window: "October – December 2026 · book before Dec 31, 2026",
    pricing_per_month: "/month",
    pricing_flat_line: "One flat rate — same price for any of the 5 formats, on any of the 4 islands.",
    pricing_min_term: "All exclusive placements require a minimum 3-month commitment.",
    calc_3month_label: "3-month minimum total",
    exclusivity_h: "Exclusive placement",
    exclusivity_p: "Each slot is exclusive: one sponsor per format, per island, for the full term of your sponsorship. When you book St. Maarten Leaderboard, no other business can show a Leaderboard on St. Maarten while you're live.",
    availability_h: "Live availability",
    availability_sub: "4 islands × 5 formats = 20 exclusive slots. Green = open. Grey = reserved.",
    availability_status: { available: "Available", sold: "Reserved" },
    availability_loading: "Loading availability…",
    creative_h: "Creative specifications",
    creative_sub: "What to send us once your slot is booked. Actual submission stays manual (email attachment to hello@islandsdaily.com) until the self-serve upload system ships in October.",
    creative_static_h: "Static creative",
    creative_animated_h: "Animated creative",
    creative_formats: "Formats accepted",
    creative_static_formats: "JPG or PNG (PNG recommended for logos and text-heavy creative)",
    creative_animated_formats: "Animated GIF only. Full HTML5/video support is planned for the self-serve system (later this year), not this build.",
    creative_max_size: "Max file size",
    creative_static_max: "200 KB per creative",
    creative_animated_max: "300 KB per creative",
    creative_loop: "Recommended loop length",
    creative_loop_value: "Under 15 seconds",
    creative_dim_h: "Exact pixel dimensions per format",
    creative_dim_note: "Creative must match the exact dimensions of the slot you booked — mismatched files get bounced back for a re-cut.",
    creative_dim_rows: {
      leaderboard:        "728 × 90",
      sidebar_tall:       "300 × 600",
      rectangle:          "300 × 250",
      sidebar_sticky:     "300 × 250",
      sponsored_category: "200 × 200 (square logo, not a banner)",
    },
    picker_multi_slot_hint: "Interested in more than one? Each additional slot is automatically 25% off.",
    calc_total_h: "Estimated monthly total",
    calc_launch_label: "Launch Partner rate (today)",
    calc_standard_label: "Standard rate (after Dec 31, 2026)",
    calc_slots_included: (n) => `${n} available slot${n === 1 ? "" : "s"} included`,
    calc_reserved_excluded: (n) => `${n} reserved slot${n === 1 ? "" : "s"} excluded from total — reach out and we'll flag if any open up.`,
    calc_hint: "First slot at full rate, each additional at 25% off. Same math the backend applies at booking.",
    calc_empty: "Pick a format and an island to see your running total.",
    picker_all_sold: "All 4 islands reserved for this format.",
    picker_all_sold_short: "All islands reserved",
    picker_some_sold: (islands) => `Reserved on: ${islands}.`,
    picker_island_all_sold: "All 5 formats reserved on this island.",
    picker_island_some_sold: (formats) => `Reserved formats: ${formats}.`,
    picker_combo_sold: (fmt, isl) => `Heads up — ${fmt} on ${isl} is already reserved.`,
    picker_combo_available: (fmt, isl) => `${fmt} on ${isl} is available.`,
    targeting_h: "Island targeting",
    targeting_p: "Every slot is booked per-island — reach only Aruba tourists, only Curaçao businesses, only St. Maarten residents. Want to reach travelers across multiple islands? Add the same or different formats on additional islands — each additional slot you hold is automatically 25% off.",
    form_h: "Get in touch",
    form_p: "Tell us who you are and what you want to promote. We reply within 48 hours with pricing and next steps.",
    form: {
      name: "Your name",
      company: "Company (optional)",
      email: "Email",
      phone: "Phone (optional)",
      slots_label: "Interested in (optional):",
      islands_label: "Island targeting (optional):",
      message: "Anything else we should know?",
      submit: "Send inquiry",
      submitting: "Sending…",
      submit_error: "Failed to send. Please try again.",
      success: "Thanks — we'll be in touch within 48 hours.",
    },
    footer_line: "IslandsDaily is a growing English-language news platform serving the Dutch Caribbean.",
  },
  nl: {
    title: "Adverteer op IslandsDaily",
    subtitle: "Bereik Engels-, Nederlands- en Spaanstalige lezers op Aruba, Curaçao, Bonaire en Sint Maarten — de toeristen, expats en inwoners die de eilanden volgen.",
    stats_h: "Actuele cijfers",
    stat_subs: "WhatsApp-abonnees",
    stat_pv: "Paginaweergaven (laatste 30 dagen)",
    stat_art: "Artikelen gepubliceerd (laatste 30 dagen)",
    stat_islands: "Eilanden gedekt",
    upcoming_h: "Binnenkort beschikbaar",
    upcoming_p: "Nieuwe eilanden lanceren binnenkort. Reserveer je advertentie tegen pre-launch prijzen voordat we live gaan.",
    wait_h: "Krijg bericht bij lancering",
    wait_p: "Laat je e-mailadres achter en we sturen een bericht zodra een nieuw eiland live gaat.",
    wait_email: "je@email.com",
    wait_pick: "Welk eiland?",
    wait_all: "Alle nieuwe eilanden",
    wait_submit: "Aanmelden",
    wait_success: "Je staat op de lijst — we mailen je bij lancering.",
    formats_h: "Beschikbare advertentieformaten",
    formats: {
      leaderboard: {
        name: "Leaderboard · 728 × 90",
        desc: "Boven de vouw op elke pagina. Zeer zichtbare banner direct onder het hero-artikel.",
      },
      rectangle: {
        name: "Rechthoek · 300 × 250",
        desc: "Bovenaan in de zijbalk. Het eerste dat lezers zien op desktop.",
      },
      sidebar_tall: {
        name: "Half-Page · 300 × 600",
        desc: "Grote verticale creatieve. Dominante plaatsing die niet te missen is.",
      },
      sidebar_sticky: {
        name: "Sticky zijbalk · 300 × 250",
        desc: "Volgt de lezer omlaag. Elk artikel = elke impressie.",
      },
      sponsored: {
        name: "Gesponsorde categorie",
        desc: "Bezit een categorie (Toerisme, Business, Sport...) met jouw merk naast elke kop erin.",
      },
    },
    pricing_h: "Tarieven",
    pricing_standard_label: "Standaardtarief",
    pricing_launch_label: "Launch Partner-tarief",
    pricing_launch_window: "Oktober – December 2026 · boek vóór 31 dec 2026",
    pricing_per_month: "/maand",
    pricing_flat_line: "Één vaste prijs — zelfde tarief voor elk van de 5 formaten, op elk van de 4 eilanden.",
    pricing_min_term: "Alle exclusieve plaatsingen vereisen een minimale looptijd van 3 maanden.",
    calc_3month_label: "3 maanden minimumtotaal",
    exclusivity_h: "Exclusieve plaatsing",
    exclusivity_p: "Elke plek is exclusief: één sponsor per formaat, per eiland, gedurende jouw volledige termijn. Boek je Sint Maarten Leaderboard, dan mag geen ander bedrijf een Leaderboard op Sint Maarten tonen zolang jij live bent.",
    availability_h: "Live beschikbaarheid",
    availability_sub: "4 eilanden × 5 formaten = 20 exclusieve plaatsen. Groen = vrij. Grijs = gereserveerd.",
    availability_status: { available: "Beschikbaar", sold: "Gereserveerd" },
    availability_loading: "Beschikbaarheid laden…",
    creative_h: "Creatievespecificaties",
    creative_sub: "Wat je ons stuurt zodra je slot geboekt is. Aanleveren blijft handmatig (e-mailbijlage naar hello@islandsdaily.com) tot het self-serve upload-systeem in oktober live gaat.",
    creative_static_h: "Statische creatieve",
    creative_animated_h: "Geanimeerde creatieve",
    creative_formats: "Geaccepteerde formaten",
    creative_static_formats: "JPG of PNG (PNG aanbevolen voor logo's en teksten)",
    creative_animated_formats: "Alleen geanimeerde GIF. Volledige HTML5/video-ondersteuning komt met het self-serve systeem later dit jaar.",
    creative_max_size: "Max bestandsgrootte",
    creative_static_max: "200 KB per creatieve",
    creative_animated_max: "300 KB per creatieve",
    creative_loop: "Aanbevolen looplengte",
    creative_loop_value: "Onder 15 seconden",
    creative_dim_h: "Exacte pixelafmetingen per formaat",
    creative_dim_note: "Creatieve moet exact overeenkomen met de afmetingen van je geboekte slot — bestanden die niet passen worden teruggestuurd voor een herbewerking.",
    creative_dim_rows: {
      leaderboard:        "728 × 90",
      sidebar_tall:       "300 × 600",
      rectangle:          "300 × 250",
      sidebar_sticky:     "300 × 250",
      sponsored_category: "200 × 200 (vierkant logo, geen banner)",
    },
    picker_multi_slot_hint: "Meer dan één interesseert je? Elke extra slot is automatisch 25% korting.",
    calc_total_h: "Geschat maandtotaal",
    calc_launch_label: "Launch Partner-tarief (vandaag)",
    calc_standard_label: "Standaardtarief (na 31 dec 2026)",
    calc_slots_included: (n) => `${n} beschikbare slot${n === 1 ? "" : "s"} inbegrepen`,
    calc_reserved_excluded: (n) => `${n} gereserveerde slot${n === 1 ? "" : "s"} niet meegerekend — laat het ons weten en we melden als er ruimte vrijkomt.`,
    calc_hint: "Eerste slot vol tarief, elke volgende 25% korting. Zelfde berekening als bij daadwerkelijke boeking.",
    calc_empty: "Kies een formaat en een eiland om het lopende totaal te zien.",
    picker_all_sold: "Alle 4 eilanden gereserveerd voor dit formaat.",
    picker_all_sold_short: "Alle eilanden gereserveerd",
    picker_some_sold: (islands) => `Gereserveerd op: ${islands}.`,
    picker_island_all_sold: "Alle 5 formaten gereserveerd op dit eiland.",
    picker_island_some_sold: (formats) => `Gereserveerde formaten: ${formats}.`,
    picker_combo_sold: (fmt, isl) => `Let op — ${fmt} op ${isl} is al gereserveerd.`,
    picker_combo_available: (fmt, isl) => `${fmt} op ${isl} is beschikbaar.`,
    targeting_h: "Eiland-targeting",
    targeting_p: "Elke slot wordt per eiland geboekt — bereik alleen toeristen op Aruba, alleen bedrijven op Curaçao, of alleen inwoners op Sint Maarten. Wil je reizigers op meerdere eilanden bereiken? Voeg dezelfde of andere formaten toe op extra eilanden — elke extra slot krijg je automatisch 25% korting.",
    form_h: "Neem contact op",
    form_p: "Vertel ons wie je bent en wat je wilt promoten. We reageren binnen 48 uur met prijzen en volgende stappen.",
    form: {
      name: "Je naam",
      company: "Bedrijf (optioneel)",
      email: "E-mail",
      phone: "Telefoon (optioneel)",
      slots_label: "Interesse in (optioneel):",
      islands_label: "Eiland-targeting (optioneel):",
      message: "Nog iets dat we moeten weten?",
      submit: "Aanvraag versturen",
      submitting: "Versturen…",
      success: "Bedankt — we nemen binnen 48 uur contact op.",
    },
    footer_line: "IslandsDaily is een groeiend Engelstalig nieuwsplatform voor het Nederlandse Caribisch gebied.",
  },
  es: {
    title: "Anunciate con IslandsDaily",
    subtitle: "Llega a lectores en inglés, neerlandés y español en Aruba, Curaçao, Bonaire y Sint Maarten — turistas, expatriados y locales que siguen las islas.",
    stats_h: "Métricas en vivo",
    stat_subs: "Suscriptores WhatsApp",
    stat_pv: "Vistas de página (últimos 30 días)",
    stat_art: "Artículos publicados (últimos 30 días)",
    stat_islands: "Islas cubiertas",
    upcoming_h: "Próximamente",
    upcoming_p: "Nuevas islas se lanzan pronto. Reserva tu espacio con precios pre-lanzamiento antes de que salgamos en vivo.",
    wait_h: "Recibe aviso al lanzar",
    wait_p: "Déjanos tu correo y te avisaremos en el momento en que una nueva isla salga en vivo.",
    wait_email: "tu@email.com",
    wait_pick: "¿Cuál isla?",
    wait_all: "Todas las nuevas islas",
    wait_submit: "Unirme a la lista",
    wait_success: "Estás en la lista — te avisaremos al lanzar.",
    formats_h: "Formatos de anuncios disponibles",
    formats: {
      leaderboard: {
        name: "Leaderboard · 728 × 90",
        desc: "Sobre el pliegue en cada página. Banner de alta visibilidad justo debajo del artículo principal.",
      },
      rectangle: {
        name: "Rectángulo · 300 × 250",
        desc: "Posición superior de la barra lateral. Lo primero que ven los lectores en escritorio.",
      },
      sidebar_tall: {
        name: "Media página · 300 × 600",
        desc: "Creatividad vertical grande. Ubicación dominante imposible de ignorar.",
      },
      sidebar_sticky: {
        name: "Barra lateral fija · 300 × 250",
        desc: "Sigue al lector al hacer scroll. Cada artículo = cada impresión.",
      },
      sponsored: {
        name: "Categoría patrocinada",
        desc: "Sé dueño de una categoría (Turismo, Negocios, Deportes...) con tu marca junto a cada titular.",
      },
    },
    pricing_h: "Precios",
    pricing_standard_label: "Tarifa estándar",
    pricing_launch_label: "Tarifa Launch Partner",
    pricing_launch_window: "Octubre – Diciembre 2026 · reserva antes del 31 dic 2026",
    pricing_per_month: "/mes",
    pricing_flat_line: "Tarifa única — mismo precio para cualquiera de los 5 formatos, en cualquiera de las 4 islas.",
    pricing_min_term: "Todas las colocaciones exclusivas requieren un compromiso mínimo de 3 meses.",
    calc_3month_label: "Total mínimo a 3 meses",
    exclusivity_h: "Colocación exclusiva",
    exclusivity_p: "Cada espacio es exclusivo: un sponsor por formato, por isla, durante todo el término. Si reservas St. Maarten Leaderboard, ningún otro negocio puede mostrar un Leaderboard en St. Maarten mientras estés activo.",
    availability_h: "Disponibilidad en vivo",
    availability_sub: "4 islas × 5 formatos = 20 espacios exclusivos. Verde = libre. Gris = reservado.",
    availability_status: { available: "Disponible", sold: "Reservado" },
    availability_loading: "Cargando disponibilidad…",
    creative_h: "Especificaciones creativas",
    creative_sub: "Qué enviarnos una vez reservado el espacio. El envío sigue siendo manual (adjunto por correo a hello@islandsdaily.com) hasta que el sistema self-serve se lance en octubre.",
    creative_static_h: "Creativo estático",
    creative_animated_h: "Creativo animado",
    creative_formats: "Formatos aceptados",
    creative_static_formats: "JPG o PNG (PNG recomendado para logotipos y textos)",
    creative_animated_formats: "Solo GIF animado. Soporte completo HTML5/video llega con el sistema self-serve más adelante este año.",
    creative_max_size: "Tamaño máximo",
    creative_static_max: "200 KB por creativo",
    creative_animated_max: "300 KB por creativo",
    creative_loop: "Duración del loop recomendada",
    creative_loop_value: "Menos de 15 segundos",
    creative_dim_h: "Dimensiones exactas por formato",
    creative_dim_note: "El creativo debe coincidir exactamente con las dimensiones del espacio reservado — archivos que no encajan se rebotan para reajuste.",
    creative_dim_rows: {
      leaderboard:        "728 × 90",
      sidebar_tall:       "300 × 600",
      rectangle:          "300 × 250",
      sidebar_sticky:     "300 × 250",
      sponsored_category: "200 × 200 (logo cuadrado, no banner)",
    },
    picker_multi_slot_hint: "¿Interesado en más de uno? Cada espacio adicional lleva un 25% de descuento automático.",
    calc_total_h: "Total mensual estimado",
    calc_launch_label: "Tarifa Launch Partner (hoy)",
    calc_standard_label: "Tarifa estándar (tras 31 dic 2026)",
    calc_slots_included: (n) => `${n} espacio${n === 1 ? "" : "s"} disponible${n === 1 ? "" : "s"} incluidos`,
    calc_reserved_excluded: (n) => `${n} espacio${n === 1 ? "" : "s"} reservado${n === 1 ? "" : "s"} excluido${n === 1 ? "" : "s"} del total — avísanos y te contactamos si alguno se libera.`,
    calc_hint: "Primer espacio tarifa completa, cada adicional 25% de descuento. Misma fórmula del backend al reservar.",
    calc_empty: "Elige un formato y una isla para ver tu total en vivo.",
    picker_all_sold: "Las 4 islas ya están reservadas para este formato.",
    picker_all_sold_short: "Todas las islas reservadas",
    picker_some_sold: (islands) => `Reservado en: ${islands}.`,
    picker_island_all_sold: "Los 5 formatos ya están reservados en esta isla.",
    picker_island_some_sold: (formats) => `Formatos reservados: ${formats}.`,
    picker_combo_sold: (fmt, isl) => `Aviso — ${fmt} en ${isl} ya está reservado.`,
    picker_combo_available: (fmt, isl) => `${fmt} en ${isl} está disponible.`,
    targeting_h: "Segmentación por isla",
    targeting_p: "Cada espacio se reserva por isla — llega solo a turistas de Aruba, solo a negocios de Curaçao, o solo a residentes de Sint Maarten. ¿Quieres llegar a viajeros en varias islas? Agrega el mismo formato u otro en más islas — cada espacio adicional lleva un 25% de descuento automático.",
    form_h: "Contáctanos",
    form_p: "Dinos quién eres y qué quieres promocionar. Respondemos en 48 horas con precios y pasos a seguir.",
    form: {
      name: "Tu nombre",
      company: "Empresa (opcional)",
      email: "Correo",
      phone: "Teléfono (opcional)",
      slots_label: "Interesado en (opcional):",
      islands_label: "Segmentación por isla (opcional):",
      message: "¿Algo más que debamos saber?",
      submit: "Enviar consulta",
      submitting: "Enviando…",
      submit_error: "Error al enviar. Inténtalo de nuevo.",
      success: "Gracias — te contactaremos en 48 horas.",
    },
    footer_line: "IslandsDaily es una creciente plataforma de noticias en inglés que cubre el Caribe holandés.",
  },
};

const numberFmt = (n) => (n || 0).toLocaleString("en-US");

export default function SponsorPage() {
  // __TOGGLE_HOOK__: self-contained reactive lang state for the mini-header toggle.
  const [__toggleLang, __setToggleLang] = useReactiveLang();

  const [lang, setLang] = useState("en");
  const [stats, setStats] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    slots: [], islands: [], message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [waitEmail, setWaitEmail] = useState("");
  const [waitIsland, setWaitIsland] = useState("");
  const [waitSubmitting, setWaitSubmitting] = useState(false);
  const [waitSubmitted, setWaitSubmitted] = useState(false);
  const [islands, setIslands] = useState([]);
  // Pricing + live availability grid — both come from the sponsor
  // ledger endpoints so numbers can't drift from what the backend
  // considers current. Refreshed on mount only (per-page cost is
  // negligible; sponsors don't hit this URL often enough for a
  // sticky-poll to be worth it).
  const [pricing, setPricing] = useState(null);
  const [slotsAvail, setSlotsAvail] = useState(null);

  useEffect(() => {
    document.title = "Advertise with IslandsDaily";
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/islands/upcoming/list").then((r) => setUpcoming(r.data || [])).catch(() => {});
    api.get("/islands").then((r) => {
      const list = (r.data || []).map((d) => ({
        slug: d.slug,
        flag: d.flag_emoji,
        name: d.name,
      }));
      setIslands(list);
    }).catch(() => {});
    api.get("/sponsor/pricing").then((r) => setPricing(r.data)).catch(() => {});
    api.get("/sponsor/slots").then((r) => setSlotsAvail(r.data)).catch(() => {});
  }, []);

  const submitWaitlist = async (e) => {
    e.preventDefault();
    if (!waitEmail.trim() || !waitEmail.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setWaitSubmitting(true);
    try {
      await api.post("/islands/waitlist", {
        email: waitEmail.trim(),
        island_slug: waitIsland,
      });
      toast.success(copy.wait_success);
      setWaitSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign-up failed");
    } finally { setWaitSubmitting(false); }
  };

  const copy = COPY[lang];

  const toggleSlot = (id) => setForm((f) => ({
    ...f, slots: f.slots.includes(id) ? f.slots.filter(x => x !== id) : [...f.slots, id],
  }));
  const toggleIsland = (slug) => setForm((f) => ({
    ...f, islands: f.islands.includes(slug) ? f.islands.filter(x => x !== slug) : [...f.islands, slug],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/sponsor-inquiry", form);
      toast.success(res.data.message || copy.form.success);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || copy.form.submit_error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="sponsor-page">
      <div className="border-b border-[#EEEEEE] bg-white sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center">
          <Link to="/" className="flex items-center gap-1.5 text-[13px] text-[#1A2E35] hover:text-[#0AADA0]" data-testid="sponsor-back-link">
            <ArrowLeft size={16} /> <span>Back</span>
          </Link>
          <Link to="/" className="ml-auto font-serif-display text-[18px] leading-none">
            <span className="text-[#1A1A1A]">Islands</span>
            <span className="text-[#0AADA0]">Daily</span>
          </Link>
          {/* __LANG_TOGGLE__ */}
          <LanguageToggle lang={__toggleLang} onChange={__setToggleLang} />
        </div>
      </div>

      <article className="mx-auto px-4 sm:px-6 py-10 sm:py-14" style={{ maxWidth: "760px" }}>
        {/* Title — IslandsDaily wordmark rendered inline in the H1 so
            the two-tone brand ("Islands" default + "Daily" teal) is
            consistent with every other page of the site. */}
        <h1 className="font-serif-display text-[32px] sm:text-[44px] leading-tight text-[#1A2E35] text-center" data-testid="sponsor-heading">
          {(() => {
            const parts = copy.title.split("IslandsDaily");
            if (parts.length !== 2) return copy.title;
            return (
              <>
                {parts[0]}
                <span className="text-[#1A2E35]">Islands</span>
                <span className="text-[#0AADA0]">Daily</span>
                {parts[1]}
              </>
            );
          })()}
        </h1>
        <p className="text-center text-[16px] text-[#6B7C82] mt-4 mb-8" style={{ lineHeight: 1.6 }}>
          {copy.subtitle}
        </p>

        {/* Language tabs */}
        <div className="flex items-center justify-center gap-2 mb-10" role="tablist">
          {LANGS.map((l) => (
            <button key={l.code} role="tab" aria-selected={lang === l.code}
              onClick={() => setLang(l.code)}
              className={`px-4 py-1.5 rounded-full text-[12px] transition-colors ${
                lang === l.code ? "bg-[#0AADA0] text-white" : "bg-[#F5F5F5] text-[#1A2E35] hover:bg-[#EEEEEE]"
              }`}
              data-testid={`sponsor-lang-${l.code}`}
            >
              {l.label}
            </button>
          ))}
        </div>

        {/* Stats */}
        <section className="mb-12" data-testid="sponsor-stats">
          <h2 className="font-serif-display text-[22px] text-[#1A2E35] mb-4">{copy.stats_h}</h2>
          {/* WhatsApp subscribers stat intentionally omitted from the
              public /sponsor page while the number is still weighted
              toward friends-and-family testers (~5). Publicly showing
              "4 subscribers" undercuts founding-sponsor conversations
              that reference the growing list. Re-add once the count
              lands in the 300–500 range already used internally as the
              founding-sponsor-conversation threshold. The underlying
              tracking is untouched — admin dashboard, per-island
              counter, and CSV export all still show it. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <StatCard icon={<TrendingUp size={16} />} label={copy.stat_pv} value={numberFmt(stats?.pageviews_last_30d)} testid="stat-pv" />
            <StatCard icon={<Newspaper size={16} />} label={copy.stat_art} value={numberFmt(stats?.articles_last_30d)} testid="stat-art" />
            <StatCard icon={<Globe2 size={16} />} label={copy.stat_islands} value={stats?.islands_covered || 4} testid="stat-islands" />
          </div>
        </section>

        {/* Ad formats */}
        <section className="mb-12" data-testid="sponsor-formats">
          <h2 className="font-serif-display text-[22px] text-[#1A2E35] mb-4">{copy.formats_h}</h2>
          <div className="space-y-3">
            {AD_SLOTS.map((s) => (
              <div key={s.id} className="flex items-start gap-3 p-4 rounded-xl border border-[#EEEEEE] hover:border-[#0AADA0]/40 transition-colors" data-testid={`sponsor-slot-${s.id}`}>
                <CheckCircle2 size={18} className="text-[#0AADA0] mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium text-[#1A2E35]">{copy.formats[s.key].name}</div>
                  <div className="text-sm text-[#6B7C82] mt-1" style={{ lineHeight: 1.5 }}>{copy.formats[s.key].desc}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Creative specifications — tells sponsors exactly what file
            to send. Display-only; actual upload lands with the
            self-serve launch (October) — until then submission is a
            manual email attachment to hello@islandsdaily.com. */}
        <section className="mb-12" data-testid="sponsor-creative-specs">
          <h2 className="font-serif-display text-[22px] text-[#1A2E35] mb-2">{copy.creative_h}</h2>
          <p className="text-[13px] text-[#6B7C82] mb-5" style={{ lineHeight: 1.5 }}>
            {copy.creative_sub}
          </p>

          {/* Format & size specs — static vs animated side-by-side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div className="p-4 rounded-xl border border-[#EEEEEE]" data-testid="creative-spec-static">
              <h3 className="text-[13px] uppercase tracking-wider text-[#0AADA0] mb-3">
                {copy.creative_static_h}
              </h3>
              <div className="space-y-2 text-[13px]">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#6B7C82]">
                    {copy.creative_formats}
                  </div>
                  <div className="text-[#1A2E35] mt-0.5" style={{ lineHeight: 1.5 }}>
                    {copy.creative_static_formats}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#6B7C82]">
                    {copy.creative_max_size}
                  </div>
                  <div className="text-[#1A2E35] mt-0.5">
                    {copy.creative_static_max}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 rounded-xl border border-[#EEEEEE]" data-testid="creative-spec-animated">
              <h3 className="text-[13px] uppercase tracking-wider text-[#0AADA0] mb-3">
                {copy.creative_animated_h}
              </h3>
              <div className="space-y-2 text-[13px]">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#6B7C82]">
                    {copy.creative_formats}
                  </div>
                  <div className="text-[#1A2E35] mt-0.5" style={{ lineHeight: 1.5 }}>
                    {copy.creative_animated_formats}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#6B7C82]">
                    {copy.creative_max_size}
                  </div>
                  <div className="text-[#1A2E35] mt-0.5">
                    {copy.creative_animated_max}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#6B7C82]">
                    {copy.creative_loop}
                  </div>
                  <div className="text-[#1A2E35] mt-0.5">
                    {copy.creative_loop_value}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Per-format pixel dimensions */}
          <div className="p-4 rounded-xl bg-[#FAFAFA]" data-testid="creative-spec-dimensions">
            <h3 className="text-[13px] uppercase tracking-wider text-[#0AADA0] mb-2">
              {copy.creative_dim_h}
            </h3>
            <p className="text-[12px] text-[#6B7C82] mb-3" style={{ lineHeight: 1.5 }}>
              {copy.creative_dim_note}
            </p>
            <table className="w-full text-left text-[13px]">
              <tbody>
                {AD_SLOTS.map((s) => (
                  <tr key={s.id} className="border-t border-[#EEEEEE] first:border-t-0">
                    <td className="py-2 pr-3 text-[#1A2E35]">
                      {copy.formats[s.key].name.split(" · ")[0]}
                    </td>
                    <td className="py-2 text-[#6B7C82] font-mono text-[12px]" data-testid={`creative-dim-${s.id}`}>
                      {copy.creative_dim_rows[s.id]}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Pricing — locked $400 standard / $200 Launch Partner. The
            Launch Partner rate stays visible while today <= 2026-12-31
            (backend `_launch_partner_active`); after that the block
            renders standard only. */}
        <section className="mb-12" data-testid="sponsor-pricing">
          <h2 className="font-serif-display text-[22px] text-[#1A2E35] mb-4">{copy.pricing_h}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {pricing?.launch_partner_active && (
              <div
                className="p-5 rounded-xl border-2 border-[#0AADA0] bg-[#0AADA0]/5 relative"
                data-testid="sponsor-pricing-launch"
              >
                <span
                  className="absolute -top-2 left-4 text-[10px] uppercase tracking-wider bg-[#0AADA0] text-white px-2 py-0.5 rounded-full"
                  data-testid="sponsor-pricing-launch-badge"
                >
                  {copy.pricing_launch_label}
                </span>
                <div className="text-[11px] uppercase tracking-wider text-[#6B7C82] mt-1">
                  {copy.pricing_launch_window}
                </div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-serif-display text-[36px] text-[#1A2E35] leading-none" data-testid="sponsor-pricing-launch-value">
                    ${pricing.launch_partner_price_usd}
                  </span>
                  <span className="text-[15px] text-[#6B7C82]">{copy.pricing_per_month}</span>
                </div>
              </div>
            )}
            <div
              className={`p-5 rounded-xl border ${pricing?.launch_partner_active ? "border-[#EEEEEE]" : "border-2 border-[#0AADA0] bg-[#0AADA0]/5"}`}
              data-testid="sponsor-pricing-standard"
            >
              <div className="text-[11px] uppercase tracking-wider text-[#6B7C82]">
                {copy.pricing_standard_label}
              </div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-serif-display text-[36px] text-[#1A2E35] leading-none" data-testid="sponsor-pricing-standard-value">
                  ${pricing?.standard_price_usd || 400}
                </span>
                <span className="text-[15px] text-[#6B7C82]">{copy.pricing_per_month}</span>
              </div>
            </div>
          </div>
          <p className="text-[13px] text-[#6B7C82] mt-3" style={{ lineHeight: 1.5 }}>
            {copy.pricing_flat_line}
          </p>
          <p className="text-[12px] text-[#B45309] mt-2 font-medium flex items-center gap-1.5" data-testid="sponsor-min-term-note">
            <Lock size={12} /> {copy.pricing_min_term}
          </p>
        </section>

        {/* Exclusivity — one sponsor per format per island. */}
        <section className="mb-12" data-testid="sponsor-exclusivity">
          <div className="flex items-start gap-3 p-5 rounded-xl bg-[#1A2E35] text-white">
            <Lock size={18} className="text-[#0AADA0] mt-0.5 shrink-0" />
            <div>
              <h3 className="font-medium text-[15px] mb-1">{copy.exclusivity_h}</h3>
              <p className="text-[13px] text-white/80" style={{ lineHeight: 1.6 }}>
                {copy.exclusivity_p}
              </p>
            </div>
          </div>
        </section>

        {/* Live availability — 4 islands × 5 formats grid pulled from
            /api/sponsor/slots. Green = available, grey = reserved. No
            sponsor names shown publicly (privacy). */}
        <section className="mb-12" data-testid="sponsor-availability">
          <h2 className="font-serif-display text-[22px] text-[#1A2E35] mb-2">{copy.availability_h}</h2>
          <p className="text-[13px] text-[#6B7C82] mb-4" style={{ lineHeight: 1.5 }}>
            {copy.availability_sub}
          </p>
          {!slotsAvail ? (
            <p className="text-[13px] text-[#6B7C82]">{copy.availability_loading}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[12px]" data-testid="sponsor-availability-grid">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#6B7C82] border-b border-[#EEEEEE]">
                    <th className="py-2 pr-3">Format</th>
                    {["aruba", "curacao", "bonaire", "stmaarten"].map((isl) => (
                      <th key={isl} className="py-2 pr-3">
                        <span className="mr-1" aria-hidden>{ISLAND_META[isl]?.flag}</span>
                        {ISLAND_META[isl]?.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {AD_SLOTS.map((s) => (
                    <tr key={s.id} className="border-b border-[#F5F5F5]">
                      <td className="py-2 pr-3 text-[#1A1A1A] whitespace-nowrap">
                        {copy.formats[s.key].name.split(" · ")[0]}
                      </td>
                      {["aruba", "curacao", "bonaire", "stmaarten"].map((isl) => {
                        const cell = slotsAvail.slots.find(
                          (c) => c.island === isl && c.format === s.id
                        );
                        const status = cell?.status || "available";
                        const isSold = status === "sold";
                        return (
                          <td key={isl} className="py-2 pr-3">
                            <span
                              className={`inline-block text-[11px] font-medium px-2.5 py-0.5 rounded-full ${
                                isSold
                                  ? "bg-[#F5F5F5] text-[#6B7280]"
                                  : "bg-[#0AADA0]/15 text-[#0AADA0]"
                              }`}
                              data-testid={`sponsor-avail-${isl}-${s.id}`}
                            >
                              {isSold
                                ? copy.availability_status.sold
                                : copy.availability_status.available}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Island targeting */}
        <section className="mb-12" data-testid="sponsor-targeting">
          <h2 className="font-serif-display text-[22px] text-[#1A2E35] mb-3">{copy.targeting_h}</h2>
          <p className="text-[15px] text-[#6B7C82]" style={{ lineHeight: 1.6 }}>{copy.targeting_p}</p>
        </section>

        {upcoming.length > 0 && (
          <section className="mb-12" data-testid="sponsor-upcoming">
            <h2 className="font-serif-display text-[22px] text-[#1A2E35] mb-3">{copy.upcoming_h}</h2>
            <p className="text-[15px] text-[#6B7C82] mb-4" style={{ lineHeight: 1.6 }}>{copy.upcoming_p}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-6">
              {upcoming.map((i) => (
                <div key={i.slug}
                     className="p-3 rounded-xl border border-[#EEEEEE] flex items-center gap-2 hover:border-[#0AADA0]/40 transition-colors"
                     style={{ borderLeft: `3px solid ${i.accent_color}` }}
                     data-testid={`upcoming-${i.slug}`}>
                  <span className="text-2xl" aria-hidden="true">{i.flag_emoji}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#1A2E35] truncate">{i.name}</div>
                    <div className="text-[11px] text-[#6B7C82]">{i.region}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Waitlist form */}
            <div className="p-5 rounded-2xl bg-[#FAFAFA] border border-[#EEEEEE]" data-testid="sponsor-waitlist">
              <h3 className="text-[15px] font-medium text-[#1A2E35] mb-1">{copy.wait_h}</h3>
              <p className="text-[13px] text-[#6B7C82] mb-4">{copy.wait_p}</p>
              {waitSubmitted ? (
                <div className="flex items-center gap-2 text-[#0AADA0] text-sm" data-testid="waitlist-success">
                  <CheckCircle2 size={16} />
                  <span>{copy.wait_success}</span>
                </div>
              ) : (
                <form onSubmit={submitWaitlist} className="flex flex-col sm:flex-row gap-2">
                  <input type="email" required value={waitEmail}
                         onChange={(e) => setWaitEmail(e.target.value)}
                         placeholder={copy.wait_email}
                         className="flex-1 bg-white rounded-lg px-4 py-2.5 text-[#1A2E35] focus:ring-2 focus:ring-[#0AADA0] focus:outline-none text-sm border border-[#EEEEEE]"
                         data-testid="waitlist-email-input" />
                  <select value={waitIsland} onChange={(e) => setWaitIsland(e.target.value)}
                          className="bg-white rounded-lg px-3 py-2.5 text-[#1A2E35] focus:ring-2 focus:ring-[#0AADA0] focus:outline-none text-sm border border-[#EEEEEE]"
                          data-testid="waitlist-island-select">
                    <option value="">{copy.wait_all}</option>
                    {upcoming.map((i) => (
                      <option key={i.slug} value={i.slug}>{i.flag_emoji} {i.name}</option>
                    ))}
                  </select>
                  <button type="submit" disabled={waitSubmitting}
                          className="px-5 py-2.5 rounded-lg bg-[#0AADA0] hover:bg-[#0AADA0]/90 text-white text-sm font-medium disabled:opacity-50 whitespace-nowrap"
                          data-testid="waitlist-submit-btn">
                    {waitSubmitting ? "…" : copy.wait_submit}
                  </button>
                </form>
              )}
            </div>
          </section>
        )}

        {/* Form */}
        <section className="mb-8" data-testid="sponsor-form">
          <h2 className="font-serif-display text-[22px] text-[#1A2E35] mb-2">{copy.form_h}</h2>
          <p className="text-[15px] text-[#6B7C82] mb-5" style={{ lineHeight: 1.6 }}>{copy.form_p}</p>

          {submitted ? (
            <div className="p-6 rounded-xl bg-[#0AADA0]/10 border border-[#0AADA0]/30 text-[#1A2E35]" data-testid="sponsor-form-success">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 size={20} className="text-[#0AADA0]" />
                <span className="font-medium">{copy.form.success}</span>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={copy.form.name} value={form.name} onChange={(v) => setForm({...form, name: v})} required testid="sponsor-input-name" />
                <Field label={copy.form.company} value={form.company} onChange={(v) => setForm({...form, company: v})} testid="sponsor-input-company" />
                <Field label={copy.form.email} value={form.email} onChange={(v) => setForm({...form, email: v})} type="email" required testid="sponsor-input-email" />
                <Field label={copy.form.phone} value={form.phone} onChange={(v) => setForm({...form, phone: v})} type="tel" testid="sponsor-input-phone" />
              </div>

              {/* Multi-slot discount hint — surfaced above the
                  format/island pickers so a prospect scanning the
                  form sees the incentive to select more than one. */}
              <p
                className="text-[12px] text-[#6B7C82]"
                style={{ lineHeight: 1.5 }}
                data-testid="sponsor-multi-slot-hint"
              >
                💡 {copy.picker_multi_slot_hint}
              </p>

              {/* Format chips — each chip surfaces how many of the
                  4 islands are already Reserved for this format so a
                  prospect knows to check availability before picking.
                  Chips are never fully disabled because a format that
                  is sold on 3 islands is still bookable on the 4th
                  — we surface the per-combo note further down when
                  both a format and an island are chosen. */}
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-[#6B7C82] mb-2">{copy.form.slots_label}</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {AD_SLOTS.map((s) => {
                    const active = form.slots.includes(s.id);
                    const soldIslands = (slotsAvail?.slots || []).filter(
                      (c) => c.format === s.id && c.status === "sold"
                    );
                    const soldNames = soldIslands
                      .map((c) => ISLAND_META[c.island]?.name)
                      .filter(Boolean);
                    const allSold = soldIslands.length >= 4;
                    return (
                      <button type="button" key={s.id} onClick={() => toggleSlot(s.id)}
                        data-testid={`sponsor-slot-toggle-${s.id}`}
                        className={`text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          allSold
                            ? "bg-[#F5F5F5] text-[#6B7280] ring-1 ring-[#EEEEEE]"
                            : active
                              ? "bg-[#0AADA0]/10 text-[#1A2E35] ring-2 ring-[#0AADA0]"
                              : "bg-[#FAFAFA] text-[#6B7C82] hover:bg-[#F0F0F0]"
                        }`}>
                        <div className="flex items-center justify-between gap-2">
                          <span>{copy.formats[s.key].name}</span>
                          {soldNames.length > 0 && (
                            <span
                              className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[#F5F5F5] text-[#6B7280] shrink-0"
                              data-testid={`sponsor-slot-toggle-${s.id}-sold-count`}
                            >
                              {allSold ? copy.picker_all_sold_short : `${soldNames.length}/4 reserved`}
                            </span>
                          )}
                        </div>
                        {soldNames.length > 0 && !allSold && (
                          <div className="text-[11px] text-[#6B7280] mt-0.5" style={{ lineHeight: 1.4 }}>
                            {copy.picker_some_sold(soldNames.join(", "))}
                          </div>
                        )}
                        {allSold && (
                          <div className="text-[11px] text-[#6B7280] mt-0.5" style={{ lineHeight: 1.4 }}>
                            {copy.picker_all_sold}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wider text-[#6B7C82] mb-2">{copy.form.islands_label}</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {islands.map((i) => {
                    const active = form.islands.includes(i.slug);
                    const soldFormats = (slotsAvail?.slots || []).filter(
                      (c) => c.island === i.slug && c.status === "sold"
                    );
                    const allSold = soldFormats.length >= 5;
                    return (
                      <button type="button" key={i.slug} onClick={() => toggleIsland(i.slug)}
                        data-testid={`sponsor-island-toggle-${i.slug}`}
                        className={`flex flex-col items-center justify-center gap-0.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                          allSold
                            ? "bg-[#F5F5F5] text-[#6B7280] ring-1 ring-[#EEEEEE]"
                            : active
                              ? "bg-[#0AADA0]/10 text-[#1A2E35] ring-2 ring-[#0AADA0]"
                              : "bg-[#FAFAFA] text-[#6B7C82] hover:bg-[#F0F0F0]"
                        }`}>
                        <span className="flex items-center gap-1.5">
                          <span>{i.flag}</span><span>{i.name}</span>
                        </span>
                        {soldFormats.length > 0 && (
                          <span
                            className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-white/60 text-[#6B7280]"
                            data-testid={`sponsor-island-toggle-${i.slug}-sold-count`}
                          >
                            {allSold ? "All reserved" : `${soldFormats.length}/5 reserved`}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Per-combo availability notes — only rendered once
                    both a format and an island are selected, so the
                    prospect gets an explicit heads-up on the exact
                    combos they've picked. */}
                {form.slots.length > 0 && form.islands.length > 0 && slotsAvail && (
                  <div className="mt-3 space-y-1" data-testid="sponsor-combo-notes">
                    {form.slots.flatMap((slotId) =>
                      form.islands.map((islandSlug) => {
                        const cell = slotsAvail.slots.find(
                          (c) => c.format === slotId && c.island === islandSlug
                        );
                        const sold = cell?.status === "sold";
                        const fmtLabel = copy.formats[
                          AD_SLOTS.find((x) => x.id === slotId)?.key
                        ]?.name.split(" · ")[0] || slotId;
                        const islLabel = ISLAND_META[islandSlug]?.name || islandSlug;
                        return (
                          <div
                            key={`${slotId}-${islandSlug}`}
                            className={`text-[11px] flex items-center gap-1.5 ${sold ? "text-[#B45309]" : "text-[#0AADA0]"}`}
                            data-testid={`sponsor-combo-${slotId}-${islandSlug}`}
                          >
                            <span aria-hidden>{sold ? "⚠" : "✓"}</span>
                            <span>
                              {sold
                                ? copy.picker_combo_sold(fmtLabel, islLabel)
                                : copy.picker_combo_available(fmtLabel, islLabel)}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                {/* Live running-total calculator. Uses the same
                    multi-slot discount rule the backend applies
                    (`sold_price_usd = base * (100-25)/100` from the
                    2nd slot on) and the same pricing endpoint
                    (`/api/sponsor/pricing`) the availability grid
                    reads. Only counts AVAILABLE combos toward the
                    total; reserved combos are listed separately so
                    the prospect knows they're not being charged for
                    something they can't have. */}
                {(() => {
                  if (!pricing || !slotsAvail) return null;
                  if (!form.slots.length || !form.islands.length) {
                    return (
                      <div
                        className="mt-4 p-3 rounded-lg bg-[#FAFAFA] text-[12px] text-[#6B7C82] text-center"
                        data-testid="sponsor-calc-empty"
                      >
                        {copy.calc_empty}
                      </div>
                    );
                  }
                  // Fan out to every (format, island) combo the
                  // prospect ticked, count available vs reserved.
                  let availableCount = 0;
                  let reservedCount = 0;
                  form.slots.forEach((slotId) => {
                    form.islands.forEach((islandSlug) => {
                      const cell = slotsAvail.slots.find(
                        (c) => c.format === slotId && c.island === islandSlug
                      );
                      if (cell?.status === "sold") reservedCount += 1;
                      else availableCount += 1;
                    });
                  });
                  // Same math as backend: 1st slot full, 2nd+ at 25% off.
                  // Flat-rate simplification: total = rate * (1 + 0.75 * (n - 1))
                  const multi = availableCount > 0
                    ? 1 + 0.75 * (availableCount - 1)
                    : 0;
                  const lpTotal = Math.round(pricing.launch_partner_price_usd * multi * 100) / 100;
                  const stdTotal = Math.round(pricing.standard_price_usd * multi * 100) / 100;
                  const lpActive = !!pricing.launch_partner_active;
                  return (
                    <div
                      className="mt-4 p-4 rounded-xl bg-[#0AADA0]/5 border border-[#0AADA0]/25"
                      data-testid="sponsor-calc"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] uppercase tracking-wider text-[#0AADA0] font-medium">
                          {copy.calc_total_h}
                        </span>
                        <span
                          className="text-[11px] text-[#6B7C82]"
                          data-testid="sponsor-calc-included"
                        >
                          {copy.calc_slots_included(availableCount)}
                        </span>
                      </div>
                      {availableCount === 0 ? (
                        <p className="text-[13px] text-[#6B7C82] italic">
                          {copy.calc_reserved_excluded(reservedCount)}
                        </p>
                      ) : (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {lpActive && (
                              <div
                                className="p-3 rounded-lg bg-white border-2 border-[#0AADA0]"
                                data-testid="sponsor-calc-launch"
                              >
                                <div className="text-[10px] uppercase tracking-wider text-[#0AADA0] font-medium">
                                  {copy.calc_launch_label}
                                </div>
                                <div className="flex items-baseline gap-1 mt-1">
                                  <span
                                    className="font-serif-display text-[28px] text-[#1A2E35] leading-none"
                                    data-testid="sponsor-calc-launch-total"
                                  >
                                    ${lpTotal}
                                  </span>
                                  <span className="text-[12px] text-[#6B7C82]">
                                    {copy.pricing_per_month}
                                  </span>
                                </div>
                              </div>
                            )}
                            <div
                              className={`p-3 rounded-lg bg-white ${lpActive ? "border border-[#EEEEEE]" : "border-2 border-[#0AADA0]"}`}
                              data-testid="sponsor-calc-standard"
                            >
                              <div className="text-[10px] uppercase tracking-wider text-[#6B7C82] font-medium">
                                {copy.calc_standard_label}
                              </div>
                              <div className="flex items-baseline gap-1 mt-1">
                                <span
                                  className="font-serif-display text-[28px] text-[#1A2E35] leading-none"
                                  data-testid="sponsor-calc-standard-total"
                                >
                                  ${stdTotal}
                                </span>
                                <span className="text-[12px] text-[#6B7C82]">
                                  {copy.pricing_per_month}
                                </span>
                              </div>
                            </div>
                          </div>
                          {/* 3-month minimum commitment total — the
                              headline number a prospect actually
                              signs for. Uses the active rate today
                              (LP if within window, otherwise Std). */}
                          <div
                            className="mt-3 p-3 rounded-lg bg-[#1A2E35] text-white flex items-baseline justify-between gap-3"
                            data-testid="sponsor-calc-3month"
                          >
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-[#0AADA0] font-medium">
                                {copy.calc_3month_label}
                              </div>
                              <div className="text-[11px] text-white/70 mt-0.5">
                                ${lpActive ? lpTotal : stdTotal} × 3 = ${(lpActive ? lpTotal : stdTotal) * 3}
                              </div>
                            </div>
                            <span
                              className="font-serif-display text-[28px] leading-none"
                              data-testid="sponsor-calc-3month-total"
                            >
                              ${(lpActive ? lpTotal : stdTotal) * 3}
                            </span>
                          </div>
                        </>
                      )}
                      {reservedCount > 0 && availableCount > 0 && (
                        <p
                          className="text-[11px] text-[#B45309] mt-3"
                          data-testid="sponsor-calc-excluded"
                        >
                          ⚠ {copy.calc_reserved_excluded(reservedCount)}
                        </p>
                      )}
                      <p className="text-[11px] text-[#6B7C82] mt-3" style={{ lineHeight: 1.5 }}>
                        {copy.calc_hint}
                      </p>
                    </div>
                  );
                })()}
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wider text-[#6B7C82] mb-2">{copy.form.message}</label>
                <textarea rows={4} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})}
                  className="w-full bg-[#FAFAFA] rounded-lg px-4 py-3 text-[#1A2E35] focus:bg-white focus:ring-2 focus:ring-[#0AADA0] focus:outline-none text-sm"
                  data-testid="sponsor-input-message" />
              </div>

              <button type="submit" disabled={submitting}
                className="w-full py-3 rounded-full bg-[#E63946] hover:bg-[#cf2f3c] text-white text-sm font-medium transition-colors disabled:opacity-60"
                data-testid="sponsor-submit-btn">
                {submitting ? copy.form.submitting : copy.form.submit}
              </button>
            </form>
          )}
        </section>

        <div className="h-px bg-[#EEEEEE] my-8" />
        <p className="text-center text-[13px] text-[#6B7C82] italic">{copy.footer_line}</p>
      </article>

      <Footer />
    </div>
  );
}

const StatCard = ({ icon, label, value, testid }) => (
  <div className="p-4 rounded-xl bg-[#FAFAFA] border border-[#EEEEEE]" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#6B7C82] mb-2">
      {icon}<span>{label}</span>
    </div>
    <div className="font-serif-display text-[28px] text-[#1A2E35]" data-testid={`${testid}-value`}>{value}</div>
  </div>
);

const Field = ({ label, value, onChange, type = "text", required = false, placeholder = "", testid }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wider text-[#6B7C82] mb-2">{label}</label>
    <input type={type} required={required} value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#FAFAFA] rounded-lg px-4 py-3 text-[#1A2E35] focus:bg-white focus:ring-2 focus:ring-[#0AADA0] focus:outline-none text-sm"
      data-testid={testid}
    />
  </div>
);
